import {EHeliMicPermissionState, onChangedMicPermissionState} from '../networklib/networkCallback';
import {GetIHeliNetwork} from '../singleton_registry';

import {getInitInteractedFlg, getMicAccessableFlg, setMicAccessableFlg} from './dataOps';
import {hel_DispatchEvent} from './moduleCaller';
import {hel_isAndroid, hel_isFirefox, hel_isLineMiniApp} from './platform';

import type {IHeliMic} from '../networklib/CHeliNetwork';

/**
 * LocalStream (GET)
 * @returns MediaStream|undefined
 */
export const getLocalStream = (): MediaStream | undefined => {
  if (globalThis.g_localStream === undefined || globalThis.g_localStream === null) {
    return undefined;
  }
  return globalThis.g_localStream;
};

let endedEventListenerAbortController: AbortController | null = null;

/**
 * LocalStream (SET)
 * @param mediaStream MediaStream
 */
export const setLocalStream = (mediaStream: MediaStream) => {
  if (mediaStream === undefined || mediaStream === null) {
    return;
  }
  const track = mediaStream.getAudioTracks()[0];
  if (track) {
    endedEventListenerAbortController?.abort();
    endedEventListenerAbortController = new AbortController();
    track.addEventListener('ended', currentAudioInputDeviceDisconnectedListener, {
      once: true,
      signal: endedEventListenerAbortController.signal
    });
  }
  globalThis.g_localStream = mediaStream;
};

/**
 * LocalStreamを閉じる
 */
export const closeLocalStream = () => {
  if (globalThis.g_localStream === undefined || globalThis.g_localStream === null) {
    return;
  }

  globalThis.g_localStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
  endedEventListenerAbortController?.abort();

  globalThis.g_localStream = null;
};

let liveLocalStream: MediaStream | null = null;
/**
 * LiveLocalStream (GET)
 * @returns MediaStreamを返す
 */
export const getLiveLocalStream = (): MediaStream | null => {
  return liveLocalStream;
};

/**
 * LiveLocalStream (SET)
 * @param mediaStream MediaStream
 */
export const setLiveLocalStream = (mediaStream: MediaStream): void => {
  liveLocalStream = mediaStream;
};

/**
 * LiveLocalStreamを閉じる
 */
export const closeLiveLocalStream = (): void => {
  if (!liveLocalStream) {
    return;
  }
  liveLocalStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
  liveLocalStream = null;
};

const getAudioInputDeviceInfos = async (): Promise<MediaDeviceInfo[]> => {
  return (await window.navigator.mediaDevices.enumerateDevices()).filter(
    (device) => device.kind === 'audioinput'
  );
};

const devicechangeEventListener = async (event: Event): Promise<void> => {
  if (!(await getAudioInputDeviceInfos())[0]) {
    return;
  }

  // 音声入力デバイスが一つでもあれば
  window.navigator.mediaDevices.removeEventListener('devicechange', devicechangeEventListener);

  const heliNetwork = GetIHeliNetwork();
  if (!heliNetwork) {
    throw new Error('GetIHeliNetwork() returns null.');
  }

  switch (heliNetwork.getMicPermissionState()) {
    case EHeliMicPermissionState.Prompt:
      try {
        // マイク許可を求める
        await window.navigator.mediaDevices.getUserMedia({audio: true});
      } catch (exception) {
        // マイク取得拒否
        await heliNetwork.checkMicPermissionState();
        return;
      }
      break;

    case EHeliMicPermissionState.Granted:
      // NOTE: 一度も実行していない場合、マイクが許可されていてもlabel、deviceIdが空文字列になる
      if (!getMicAccessableFlg()) {
        await window.navigator.mediaDevices.getUserMedia({audio: true});
      }
      break;
  }

  // マイクが許可されれば
  openSelectMicDialog();
  // 左上UIのマイク無効を解除
  onChangedMicPermissionState(heliNetwork.getMicPermissionState());
};

const openSelectMicDialog = (): void => {
  hel_DispatchEvent('ui-open-select-mic-dialog', '');
};

const currentAudioInputDeviceDisconnectedListener = async () => {
  closeLocalStream();
  closeLiveLocalStream();

  const micDeviceList = await hel_net_get_mic_device_list();
  if (!micDeviceList) {
    // マイク未許可時
    return;
  }

  const heliNetwork = GetIHeliNetwork();
  if (!heliNetwork) {
    throw new Error('GetIHeliNetwork() returns null.');
  }

  hel_DispatchEvent('ui-set-mic-ui-to-off', '');
  await heliNetwork.setMicEnabled(false);
  await heliNetwork.setLiveMicEnabled(false);

  if (micDeviceList[0]) {
    // マイクが1つ以上存在すれば
    openSelectMicDialog();
  } else {
    // マイクが1つも存在しなければ
    // 左上UIのマイク無効
    onChangedMicPermissionState(heliNetwork.getMicPermissionState());
  }
};

/**
 * 音声入力デバイスの一覧取得
 * @returns マイク未許可時は `null`。
 */
export const hel_net_get_mic_device_list = async (): Promise<IHeliMic[] | null> => {
  const heliNetwork = GetIHeliNetwork();
  if (!heliNetwork) {
    throw new Error('GetIHeliNetwork() returns null.');
  }
  await heliNetwork.checkMicPermissionState();
  if (heliNetwork.getMicPermissionState() !== EHeliMicPermissionState.Granted) {
    return null;
  }
  if (!(await getAudioInputDeviceInfos())[0]) {
    // 音声入力デバイスが一つもない場合、デバイス構成の変化を監視
    window.navigator.mediaDevices.addEventListener('devicechange', devicechangeEventListener);
    return [];
  }

  // NOTE: Firefoxの場合、マイクが許可されていてもlabel、deviceIdが空文字列になるの getUserMediaを実行する
  if (!getMicAccessableFlg() || hel_isFirefox()) {
    await window.navigator.mediaDevices.getUserMedia({audio: true});
  }

  return (await getAudioInputDeviceInfos()).map((device) => ({
    label: device.label || 'unnamed device', // デバイス名が空の場合、仮の名称を付ける
    deviceId: device.deviceId
  }));
};

/**
 * 初回タップの判定を取れるまで待機させる関数
 */
const waitForInteraction = (): Promise<void> => {
  return new Promise((resolve) => {
    const check = () => {
      if (getInitInteractedFlg()) {
        resolve();
      } else {
        // フラグが false の場合、指定時間後に再度チェック
        setTimeout(check, 50);
      }
    };
    // 最初のチェックを開始
    check();
  });
};

/**
 * 音声入力デバイスの取得可否の判定
 * @returns boolean: MediaStreamが作成できたらアクセス可能の判定しtrueを返す
 */
export const hel_net_can_access_microphone = async (): Promise<boolean> => {
  // ※暫定不具合回避実装（Android版ミニアプリ対応）
  // Android版ミニアプリのみ、初回タップ（CLICK TO PLAY）を検知するまで処理を保留する
  if (hel_isAndroid() && hel_isLineMiniApp()) {
    await waitForInteraction();
  }
  // ※暫定不具合回避実装（Android版ミニアプリ対応）
  // 既に初期化済みであればフラグを見てtrueを返す
  // （Android版LINEミニアプリはpermission.queryが常にpromptになり、そのままだと大量の確認ダイアログが発生するため）
  if (getMicAccessableFlg()) {
    return true;
  }
  // まず Permission API が使えるか確認
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const permissionStatus = await navigator.permissions.query({name: 'microphone'});
      if (permissionStatus.state === 'granted') {
        setMicAccessableFlg(true);
        return true;
      }
      // granted 以外は実際に getUserMedia を試す
    } catch (e) {
      // query が失敗した場合は無視して getUserMedia 生成による判定に進む
    }
  }
  // MediaStreamが生成されたらアクセス可能判定
  try {
    const stream = await navigator.mediaDevices.getUserMedia({audio: true});
    stream.getTracks().forEach((track) => track.stop()); // 即解放
    setMicAccessableFlg(true);
    return true;
  } catch (err) {
    return false;
  }
};
